/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { InventoryTag } from 'sarsaparilla';

import type { SearchResultItem } from '../../api/search/search.types';

import styles from './CardInventoryTag.module.scss';

interface CardInventoryTagProps {
    item: SearchResultItem;
    hasText: boolean;
    pillClassName?: string;
    pillListClassName?: string;
}

export function CardInventoryTag({
    item,
    hasText,
    pillClassName,
    pillListClassName,
}: CardInventoryTagProps) {
    const memoizedPills = React.useMemo(() => {
        if (!item?.entity_type) return [];

        const pills: React.ReactNode[] = [];

        switch (item.entity_type) {
            case 'recarea': {
                pills.push(
                    <InventoryTag.InventoryRecArea
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="recarea"
                    />
                );
                break;
            }
            case 'permit': {
                if (item.permit_type === 'hunting') {
                    pills.push(
                        <InventoryTag.InventoryHuntingPermit
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key="hunting-permit"
                        />
                    );
                } else {
                    pills.push(
                        <InventoryTag.InventoryPermit
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key="permit"
                        />
                    );
                }
                break;
            }
            case 'vehiclepermit':
                pills.push(
                    <InventoryTag.InventoryPermit
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="permit"
                    />
                );
                break;
            case 'campground':
                pills.push(...getCampgroundPills(item, hasText, pillClassName));
                break;
            case 'venuereservations':
            case 'venuereservations_venue':
                pills.push(...getVenuePills(item, hasText, pillClassName));
                break;
            case 'ticket':
            case 'ticketfacility':
            case 'tour':
            case 'timedentry':
            case 'timedentry_tour':
                pills.push(
                    <InventoryTag.InventoryTicket
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="ticket"
                    />
                );
                break;
            case 'activitypass':
                pills.push(
                    <InventoryTag.InventoryActivityPass
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        name={item?.activity_category_type || 'Activity Pass'}
                        className={pillClassName}
                        key="activityPass"
                    />
                );
                break;
            case 'treepermit':
                pills.push(
                    <InventoryTag.InventoryTreePermits
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="treePermit"
                    />
                );
                break;
            case 'giftcard':
                pills.push(
                    <InventoryTag.InventoryGiftCard
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="giftCard"
                    />
                );
                break;
            default:
                pills.push(
                    <InventoryTag.InventoryPOI
                        hasText={hasText}
                        isInverse={!hasText}
                        size={hasText ? undefined : 'sm'}
                        className={pillClassName}
                        key="poi"
                    />
                );
        }

        if (item.park_pass_facility_id) {
            pills.push(
                <InventoryTag.InventorySitePass
                    hasText={hasText}
                    isInverse={!hasText}
                    size={hasText ? undefined : 'sm'}
                    className={pillClassName}
                    key="parkPass"
                />
            );
        }

        if (item.has_lottery) {
            pills.push(
                <InventoryTag.InventoryLottery
                    hasText={hasText}
                    isInverse={!hasText}
                    size={hasText ? undefined : 'sm'}
                    className={pillClassName}
                    key="lottery"
                />
            );
        }

        return pills;
    }, [item, hasText, pillClassName]);

    return (
        <div
            className={cx(styles.pillsList, pillListClassName)}
            role={memoizedPills.length > 1 ? 'list' : undefined}
        >
            {/* if there is only one list item, it becomes a span and parent is a div */}
            {memoizedPills.length === 1
                ? React.cloneElement(memoizedPills[0] as React.ReactElement, {
                      isSpanItem: true,
                  })
                : memoizedPills}
        </div>
    );
}

// Helper for campground pills
function getCampgroundPills(
    item: SearchResultItem,
    hasText: boolean,
    pillClassName?: string
) {
    if (
        item.campsite_type_of_use &&
        item.campsite_type_of_use.length > 0 &&
        item.campsite_type_of_use[0] !== 'na'
    ) {
        return item.campsite_type_of_use
            .map((type, idx) => {
                if (type === 'Day') {
                    return (
                        <InventoryTag.InventoryDayUse
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                }

                if (type === 'Overnight') {
                    return (
                        <InventoryTag.InventoryCamping
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                }

                return null; // remove unsupported types
            })
            .filter(Boolean); // dump nulls
    }
    // Default: assume a campground
    return [
        <InventoryTag.InventoryCamping
            hasText={hasText}
            isInverse={!hasText}
            size={hasText ? undefined : 'sm'}
            className={pillClassName}
            key={0}
        />,
    ];
}

// Helper for venue pills
function getVenuePills(item: SearchResultItem, hasText: boolean, pillClassName?: string) {
    if (item.venue_tags && item.venue_tags.length > 0) {
        return item.venue_tags.map((type, idx) => {
            switch (type) {
                case 'wedding-ceremony':
                    return (
                        <InventoryTag.InventoryVenueWedding
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'large-reception':
                    return (
                        <InventoryTag.InventoryVenueLargeReception
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'small-gathering':
                    return (
                        <InventoryTag.InventoryVenueSmallGathering
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'golfing':
                    return (
                        <InventoryTag.InventoryVenueGolfing
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'shooting':
                    return (
                        <InventoryTag.InventoryVenueShooting
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'hunting':
                    return (
                        <InventoryTag.InventoryVenueHunting
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'athletic-activity':
                    return (
                        <InventoryTag.InventoryVenueAthleticActivity
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                case 'other':
                    return (
                        <InventoryTag.InventoryVenueOther
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
                default:
                    return (
                        <InventoryTag.InventoryVenueOther
                            name={type}
                            hasText={hasText}
                            isInverse={!hasText}
                            size={hasText ? undefined : 'sm'}
                            className={pillClassName}
                            key={idx}
                        />
                    );
            }
        });
    }
    // Default: assume other
    return [
        <InventoryTag.InventoryVenueOther
            hasText={hasText}
            isInverse={!hasText}
            size={hasText ? undefined : 'sm'}
            className={pillClassName}
            key={0}
        />,
    ];
}

// cSpell:ignore recarea venuereservations venuereservations_venue vehiclepermit ticketfacility timedentry timedentry_tour giftcard treepermit activitypass
